import { HttpParams } from '@angular/common/http'
import { Component, DestroyRef, OnDestroy, inject, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { CellEditEndingEventArgs, CellRangeEventArgs, CellType, Column, DataMap, FlexGrid, FormatItemEventArgs } from '@grapecity/wijmo.grid'

import { BaseComponent, FeatureType, Grid, ListItem, ModalSize, PunchListItem, User, VpJob } from '../../shared'
import { ActionToolbarButtonsComponent } from '../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { GridExportComponent } from '../../shared/reusable-components/grid-export/grid-export.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { PunchListItemComponent } from './punch-list-item.component'
import type { PunchListFirmRow } from './punch-list.types'

@Component({
    selector: 'app-punch-list-items',
    templateUrl: './punch-list-items.component.html',
    styleUrls: ['./punch-list-items.component.css'],
    standalone: true,
    imports: [
        FormsModule,
        ActionToolbarCount,
        ActionToolbarButtonsComponent,
        ActionToolbarSearchComponent,
        GridExportComponent,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
        WjInputModule,
    ],
})
export class PunchListItemsComponent extends BaseComponent implements OnDestroy {
    private destroyRef = inject(DestroyRef)
    private route = inject(ActivatedRoute)
    private router = inject(Router)

    grid: Grid<PunchListItem>

    selectedJob = signal<string>('')
    jobMap: ListItem<string>[] = []
    allJobs: VpJob[] = []
    firms: PunchListFirmRow[] = []
    allUsers: User[] = []
    activeUsers: User[] = []
    allUserMap = new DataMap([], 'id', 'displayName')
    activeUserMap = new DataMap([], 'id', 'displayName')

    firmDisplayMap = new DataMap([], 'value', 'display')
    doneMap = new DataMap([new ListItem(true, 'Yes'), new ListItem(false, 'No')], 'value', 'display')
    pasteItemIds: string[] = []

    private lastEditBegin: { id: string; binding: string; value: unknown } | null = null
    private flexGridFormatItemWired = false

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.FieldToolsPunchListEdit])
    }

    get canToggleDone() {
        return this.services.authentication.hasFeature([
            FeatureType.FieldToolsPunchListView,
            FeatureType.FieldToolsPunchListEdit,
        ])
    }

    get selectedJobName(): string {
        const jobId = this.selectedJob()
        if (!jobId) return ''
        const job = this.allJobs.find(j => j.id === jobId)
        return job ? `${job.id} ${job.name}` : jobId
    }

    constructor() {
        super()
        this.grid = new Grid<PunchListItem>(this.services, 'id', this)
            .setupModalEditing(
                this.services.data.punchListItems,
                'Punch List Item',
                PunchListItemComponent,
                () => new PunchListItem(),
                this,
                ModalSize.large,
            )
            .filterBy(item => [
                item.item,
                item.location,
                item.scope,
                item.priority,
                item.projectMateNumber,
                item.description,
                item.notes,
                item.done === true ? 'Yes' : item.done === false ? 'No' : '',
                this.externalFirmDisplay(item),
                this.contactDisplay(item),
                this.vanConResponsibleDisplay(item),
                item.completionComment,
            ])
            .isReadOnly(false)
            .editOnDoubleClick(false)
            .wrapColumnHeaders()

        this.grid.itemAdded.on(this.onItemAdded)
        this.grid.modified.on(this.onGridModified)
        this.grid.canEdit = this.canToggleDone
        // Bid History uses native FlexGrid double-click (no custom handler); only wire contact display.
        this.grid.initialized.on(g => this.wireFlexGridDisplayHooks(g.flexGrid))
        this.refresh()
    }

    private readonly onItemAdded = (item: PunchListItem) => {
        item.job = this.selectedJob()
    }

    private readonly onGridModified = () => {
        this.refreshItems()
    }

    ngOnDestroy() {
        const flex = this.grid.flexGrid
        if (flex && this.flexGridFormatItemWired) {
            flex.formatItem.removeHandler(this.onFlexFormatItem)
            this.flexGridFormatItemWired = false
        }
        this.grid.itemAdded.off(this.onItemAdded)
        this.grid.modified.off(this.onGridModified)
        this.grid.destroy()
    }

    private wireFlexGridDisplayHooks(flex: FlexGrid) {
        const contactCol = flex.getColumn('contactId')
        if (contactCol) contactCol.dataMap = null
        if (!this.canEdit) {
            for (const col of flex.columns) {
                if (col.binding !== 'done') {
                    col.isReadOnly = true
                }
            }
        }
        if (!this.flexGridFormatItemWired) {
            flex.formatItem.addHandler(this.onFlexFormatItem)
            this.flexGridFormatItemWired = true
        }
    }

    private onFlexFormatItem = (flex: FlexGrid, e: FormatItemEventArgs) => {
        if (e.panel.cellType !== CellType.Cell) return
        const item = e.panel.rows[e.row]?.dataItem as PunchListItem | undefined
        e.cell.classList.toggle('punch-list-done', item?.done === true)

        const col = flex.columns[e.col]
        if (!col || col.binding !== 'contactId') return
        if (!item) return
        if (flex.activeEditor && flex.selection.row === e.row && flex.selection.col === e.col) return
        e.cell.textContent = this.contactDisplay(item)
    }

    refreshItems() {
        const jobId = this.selectedJob()
        if (jobId && jobId !== '') {
            this.loadPunchData(jobId)
        } else {
            this.initialLoadingComplete()
        }
    }

    refresh() {
        const sub = this.services.data
            .load(
                true,
                true,
                this.services.data.vpJobs.queryAll(),
                this.services.data.get<PunchListFirmRow[]>('api/VpOwner/GetFirms'),
                this.services.data.users.queryAllByAction('GetForLookup'),
            )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: result => {
                    this.allJobs = result[0] as VpJob[]
                    this.buildJobMap()
                    this.firms = result[1] ?? []
                    this.allUsers = result[2] ?? []
                    this.allUserMap = new DataMap(this.allUsers, 'id', 'displayName')
                    this.activeUsers = this.allUsers.filter(u => u.active)
                    this.activeUserMap = new DataMap(this.activeUsers, 'id', 'displayName')
                    this.buildFirmDisplayMap()
                    this.tryOpenItemFromQuery()
                    const jobId = this.selectedJob()
                    if (jobId && jobId !== '') {
                        this.loadPunchData(jobId)
                    } else {
                        this.initialLoadingComplete()
                    }
                },
            })
        this.cancelableRequest('', true, sub)
    }

    buildJobMap() {
        const jobMap: ListItem<string>[] = []
        jobMap.push(new ListItem<string>('', 'Select a Project'))
        this.allJobs.forEach(job => {
            jobMap.push(new ListItem<string>(job.id, job.id + ' ' + job.name))
        })
        this.jobMap = jobMap
    }

    buildFirmDisplayMap() {
        const entries: { value: number | null; display: string }[] = [{ value: null, display: 'VanCon Responsible' }]
        for (const f of this.firms) {
            if (f.name?.trim().toLowerCase() === 'vancon') continue
            entries.push({ value: f.id, display: f.name })
        }
        this.firmDisplayMap = new DataMap(entries, 'value', 'display')
    }

    private contactOptionsFor(item: PunchListItem): ListItem<number | null>[] {
        if (item.responsibleFirmId == null) return [new ListItem<number | null>(null, '(none)')]
        const firm = this.firms.find(x => x.id === item.responsibleFirmId)
        const options = [new ListItem<number | null>(null, '(none)')]
        if (firm?.contacts?.length) {
            options.push(
                ...firm.contacts
                    .map(c => new ListItem<number | null>(c.contactCode, this.contactLabel(c)))
                    .sort((a, b) => a.display.localeCompare(b.display)),
            )
        }
        return options
    }

    private contactLabel(contact: NonNullable<PunchListFirmRow['contacts']>[number]): string {
        const parts = [contact.firstName, contact.lastName].filter(Boolean)
        if (parts.length > 0) return parts.join(' ')
        if (contact.sortName) return contact.sortName
        return String(contact.contactCode)
    }

    private normalizeContact(item: PunchListItem) {
        if (item.responsibleFirmId == null) {
            item.contactId = null
            return
        }
        const options = this.contactOptionsFor(item)
        if (item.contactId != null && !options.some(o => o.value === item.contactId)) {
            item.contactId = null
        }
    }

    /** Match modal: rows may still store VanCon vendor firm id — use null sentinel so firm DataMap applies. */
    private applyLegacyVanConFirmNormalization(items: PunchListItem[]) {
        const vanConId = this.firms.find(f => f.name?.trim().toLowerCase() === 'vancon')?.id
        if (vanConId == null) return
        for (const item of items) {
            if (item.responsibleFirmId === vanConId) {
                item.responsibleFirmId = null
                item.contactId = null
            }
        }
    }

    /** External trade firm label for grid/search (sentinel = VanCon-only tracking). */
    externalFirmDisplay(item: PunchListItem): string {
        const vanConId = this.firms.find(f => f.name?.trim().toLowerCase() === 'vancon')?.id
        if (item.responsibleFirmId == null || (vanConId != null && item.responsibleFirmId === vanConId)) {
            return 'VanCon Responsible'
        }
        return this.firmDisplayMap.getDisplayValue(item.responsibleFirmId) ?? ''
    }

    vanConResponsibleDisplay(item: PunchListItem): string {
        const id = item.vanConResponsibleUserId
        if (id == null || id === '') return ''
        return this.allUserMap.getDisplayValue(id) ?? id
    }

    onJobChanged() {
        const jobId = this.selectedJob()
        if (!jobId || jobId === '') {
            this.grid.items = []
            return
        }
        this.loadPunchData(jobId)
        this.scheduleGridLayout()
    }

    /** The grid is hidden until a job is selected, so autosize runs before it is on screen. */
    private scheduleGridLayout() {
        setTimeout(() => this.services.utility.raiseLayoutRequiresUpdate(), 0)
        setTimeout(() => this.services.utility.raiseLayoutRequiresUpdate(), 100)
    }

    loadPunchData(jobId: string) {
        const params = new HttpParams().set('jobId', jobId)
        this.services.data
            .get<PunchListItem[]>('api/PunchList/GetByJob', params, PunchListItem)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: rows => {
                    const list = rows ?? []
                    this.applyLegacyVanConFirmNormalization(list)
                    this.grid.items = list
                    this.initialLoadingComplete()
                    this.scheduleGridLayout()
                    this.tryOpenItemAfterLoad()
                },
                error: () => {
                    this.services.notification.error('Failed to load punch list')
                    this.grid.items = []
                    this.initialLoadingComplete()
                    this.scheduleGridLayout()
                },
            })
    }

    contactDisplay(item: PunchListItem): string {
        if (item.contactId == null || item.responsibleFirmId == null) return ''
        const vanConId = this.firms.find(f => f.name?.trim().toLowerCase() === 'vancon')?.id
        if (vanConId != null && item.responsibleFirmId === vanConId) return ''
        const firm = this.firms.find(f => f.id === item.responsibleFirmId)
        const contact = firm?.contacts?.find(x => x.contactCode === item.contactId)
        if (!contact) return String(item.contactId)
        return this.contactLabel(contact)
    }

    async openImportModal() {
        const jobId = this.selectedJob()
        if (!jobId) return

        const { PunchListImportComponent } = await import('./punch-list-import.component')
        const ref = this.services.modals.open(
            PunchListImportComponent,
            PunchListImportComponent.modalSize,
            PunchListImportComponent.modalBackdrop,
        )
        const modal = ref.componentInstance as InstanceType<typeof PunchListImportComponent>
        modal.jobId = jobId
        modal.jobLabel = this.selectedJobName

        ref.result.then(
            (result: { saved?: boolean } | undefined) => {
                if (result?.saved) {
                    this.loadPunchData(jobId)
                }
            },
            () => undefined,
        )
    }

    private pendingOpenItemId: string | null = null

    private tryOpenItemFromQuery() {
        const openId = this.route.snapshot.queryParamMap.get('openPunchListItemId')
        const jobId = this.route.snapshot.queryParamMap.get('jobId')
        if (!openId) return

        this.pendingOpenItemId = openId

        if (jobId && jobId !== this.selectedJob()) {
            this.selectedJob.set(jobId)
            this.loadPunchData(jobId)
        }
    }

    private tryOpenItemAfterLoad() {
        const openId = this.pendingOpenItemId
        if (!openId) return
        this.pendingOpenItemId = null

        const clearParams = () => {
            void this.router.navigate([], {
                relativeTo: this.route,
                queryParams: { openPunchListItemId: null, jobId: null },
                queryParamsHandling: 'merge',
                replaceUrl: true,
            })
        }

        const gridItem = this.grid.items.find(i => String(i.id) === openId)
        if (!gridItem) {
            this.services.notification.warning('Punch list item no longer exists')
            clearParams()
            return
        }

        this.grid.selectedItem = gridItem
        setTimeout(() => {
            this.grid.editItem(gridItem)
            clearParams()
        }, 0)
    }

    cellBeginningEdit(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        const col = flexGrid.columns[args.col] as Column
        const item = flexGrid.rows[args.row].dataItem as PunchListItem
        if (col.binding) {
            this.lastEditBegin = { id: item.id, binding: col.binding, value: (item as any)[col.binding] }
        } else {
            this.lastEditBegin = null
        }
        if (col.binding === 'vanConResponsibleUserId') {
            col.dataMap = this.activeUserMap
        } else if (col.binding === 'contactId') {
            col.dataMap = new DataMap(this.contactOptionsFor(item), 'value', 'display')
        }
    }

    cellEditEnded(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        const col = flexGrid.columns[args.col] as Column

        if (col.binding === 'vanConResponsibleUserId') col.dataMap = this.allUserMap
        if (col.binding === 'contactId') col.dataMap = null

        if (args.cancel) {
            this.lastEditBegin = null
            return
        }

        const item = flexGrid.rows[args.row].dataItem as PunchListItem
        if (col.binding === 'responsibleFirmId' || col.binding === 'contactId') {
            this.normalizeContact(item)
        }

        let valueChanged = true
        const binding = col.binding
        if (this.lastEditBegin && this.lastEditBegin.id === item.id && binding === this.lastEditBegin.binding) {
            const newVal = binding ? (item as any)[binding] : undefined
            valueChanged = !Object.is(newVal, this.lastEditBegin.value)
        }
        this.lastEditBegin = null

        if (!valueChanged) return

        if (!this.canEdit) {
            this.setDone(item)
            return
        }

        this.updatePunchListItems([item])
    }

    pasting(flexGrid: FlexGrid, args: CellRangeEventArgs) {
        if (args.cancel) return
        const rng = args.range
        const itemIds: string[] = []
        let row = Math.min(rng.row, rng.row2)
        const row2 = Math.max(rng.row, rng.row2)
        for (row; row <= row2; row++) {
            const dataItem = flexGrid.rows[row].dataItem as PunchListItem
            itemIds.push(dataItem.id)
        }
        this.pasteItemIds = itemIds
    }

    pasted(flexGrid: FlexGrid, args: CellRangeEventArgs) {
        if (args.cancel) return
        const itemsToSave = flexGrid.collectionView.items.filter((x: PunchListItem) => this.pasteItemIds.indexOf(x.id) > -1)
        itemsToSave.forEach((item: PunchListItem) => this.normalizeContact(item))
        if (!this.canEdit) {
            itemsToSave.forEach((item: PunchListItem) => this.setDone(item))
        } else {
            this.updatePunchListItems(itemsToSave)
        }
        this.pasteItemIds = []
    }

    private setDone(item: PunchListItem) {
        const params = new HttpParams().set('id', item.id).set('done', item.done === true)
        this.services.data.put('api/PunchList/SetDone', null, params).subscribe({
            error: () => {
                this.services.notification.error('The punch list changes were not successfully saved')
            },
        })
    }

    updatePunchListItems(items: PunchListItem[]) {
        /** Match Bid History `updateBids`: do not reload the grid on success (avoids killing the editor / selection). */
        this.services.data.put('api/PunchList/BulkUpdate', items).subscribe({
            error: () => {
                this.services.notification.error('The punch list changes were not successfully saved')
            },
        })
    }
}
